import { type JSX, useId, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import {
  aufteilen,
  vorgaenge as ladeVorgaenge,
  zusammenfuehren,
  type Nachrichtzeile,
  type Vorgangszeile,
} from '../api/support';
import { alsZeitpunkt } from '../datum';
import { Abschlussreihe } from '../komponenten/Abschlussreihe';
import { Dialog } from '../komponenten/Dialog';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { fange } from './bearbeitungshilfe';
import { statusmarke } from './Zuordnung';

/**
 * ZUSAMMENFÜHREN UND ABTRENNEN (SUP-15; Auftrag CRM-178).
 *
 * Zusammengeführt wird nur innerhalb desselben Postfachs: Dieser Vorgang
 * geht im gewählten auf. Abgetrennt wird eine eingehende Nachricht, die ein
 * eigenes Anliegen ist — sie wird ein neuer Vorgang im selben Postfach.
 * Beides fragt vorher nach.
 */

export function Zusammenfuehrdialog({
  vorgang: v,
  beiZusammengefuehrt,
  beiSchliessen,
}: {
  readonly vorgang: Vorgangszeile;
  /** Mit dem Vorgang, in dem dieser aufging. */
  readonly beiZusammengefuehrt: (ziel: { readonly vorgang: string; readonly nummer: string }) => void;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const kennung = useId();
  const [suche, setzeSuche] = useState('');
  const [treffer, setzeTreffer] = useState<readonly Vorgangszeile[] | null>(null);
  const [ziel, setzeZiel] = useState<Vorgangszeile | null>(null);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);

  const suchen = async (): Promise<void> => {
    if (suche.trim() === '') return;
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      // SUP-15: nur Vorgänge desselben Postfachs.
      const antwort = await ladeVorgaenge({ postfach: v.postfach, suche: suche.trim() });
      setzeTreffer(antwort.vorgaenge.filter((t) => t.id !== v.id));
    } catch (f) {
      setzeTreffer([]);
      fange(setzeFehler, f);
    } finally {
      setzeLaeuft(false);
    }
  };

  if (ziel !== null) {
    return (
      <Rueckfrage
        frage="Vorgänge zusammenführen?"
        unterzeile={`${v.nummer} geht in ${ziel.nummer} auf`}
        tat="Zusammenführen"
        beiBestaetigen={async () => {
          const ergebnis = await zusammenfuehren(v.id, ziel.id);
          beiZusammengefuehrt(ergebnis);
        }}
        beiSchliessen={() => setzeZiel(null)}
      >
        <p>
          Nachrichten, Notizen und Bezüge von {v.nummer} stehen danach in {ziel.nummer} („{ziel.betreff}"). {v.nummer}
          {' '}verweist dann nur noch dorthin. Das lässt sich nicht zurücknehmen.
        </p>
      </Rueckfrage>
    );
  }

  return (
    <Dialog titel="Mit anderem Vorgang zusammenführen" unterzeile={`${v.nummer} im selben Postfach`} breite="mittel" beiSchliessen={beiSchliessen}>
      <>
        {fehler !== null && <Meldung fehler={fehler} />}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void suchen();
          }}
        >
          <div className="feld">
            <label className="feld__beschriftung" htmlFor={`${kennung}-suche`}>Vorgangsnummer oder Betreff</label>
            <input
              id={`${kennung}-suche`}
              className="feld__eingabe"
              value={suche}
              onChange={(e) => setzeSuche(e.target.value)}
            />
          </div>
          <div className="knopfreihe">
            <button type="submit" className="knopf knopf--klein" disabled={laeuft || suche.trim() === ''}>
              Suchen
            </button>
          </div>
        </form>
        {treffer !== null && treffer.length === 0 && (
          <p className="feld__hilfe">Kein anderer Vorgang in diesem Postfach passt.</p>
        )}
        {treffer !== null && treffer.length > 0 && (
          <ul className="zuordnung__bezuege" aria-label="Gefundene Vorgänge">
            {treffer.map((t) => (
              <li key={t.id}>
                <strong>{t.nummer}</strong> {t.betreff} {statusmarke(t.status)}
                <br />
                <span className="hilfe">
                  {t.absendername ?? t.absender} · {alsZeitpunkt(t.letzteNachrichtAm) ?? t.letzteNachrichtAm}
                </span>{' '}
                <button type="button" className="knopf knopf--klein" onClick={() => setzeZiel(t)}>
                  Wählen
                </button>
              </li>
            ))}
          </ul>
        )}
        <Abschlussreihe
          abbruch={
            <button type="button" className="knopf" onClick={beiSchliessen}>
              Abbrechen
            </button>
          }
        >
          {null}
        </Abschlussreihe>
      </>
    </Dialog>
  );
}

/** SUP-15: eine eingehende Nachricht als eigenen Vorgang abtrennen. */
export function Abtrennen({
  nachricht: n,
  beiAbgetrennt,
}: {
  readonly nachricht: Nachrichtzeile;
  readonly beiAbgetrennt: (neu: { readonly vorgang: string; readonly nummer: string }) => Promise<void> | void;
}): JSX.Element {
  const [frage, setzeFrage] = useState(false);
  return (
    <>
      <div className="knopfreihe">
        <button type="button" className="knopf knopf--klein" onClick={() => setzeFrage(true)}>
          Als eigenen Vorgang abtrennen …
        </button>
      </div>
      {frage && (
        <Rueckfrage
          frage="Nachricht abtrennen?"
          unterzeile={`${n.betreff || '(ohne Betreff)'} vom ${alsZeitpunkt(n.eingegangenAm) ?? n.eingegangenAm}`}
          tat="Abtrennen"
          beiBestaetigen={async () => {
            const neu = await aufteilen(n.id);
            setzeFrage(false);
            await beiAbgetrennt(neu);
          }}
          beiSchliessen={() => setzeFrage(false)}
        >
          <p>
            Die Nachricht wird ein eigener Vorgang im selben Postfach, mit eigenem Status und eigener Zuständigkeit.
            In diesem Vorgang steht sie danach nicht mehr.
          </p>
        </Rueckfrage>
      )}
    </>
  );
}
